import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { isEvidenceWorkItemStatus } from "./assessment_work_items.types";
import type { EvidenceRequirementStatus } from "./evidence_requirements.service";
import { LoginService } from "./login.service";

export type EvidenceRequirementChangeKind = "create" | "update" | "delete" | "bulk" | "regenerate" | "upload" | "comment";

export interface EvidenceRequirementChangeScope {
    // Null means the previous or current scope is unknown; consumers refresh conservatively.
    requirementKeys: readonly string[] | null;
    assignedUserIds: readonly number[] | null;
    status: EvidenceRequirementStatus | null;
}

export interface EvidenceRequirementAffectedItem {
    id: number;
    before: EvidenceRequirementChangeScope | null;
    after: EvidenceRequirementChangeScope | null;
}

export interface EvidenceRequirementChange {
    kind: EvidenceRequirementChangeKind;
    productLicenseId: number;
    // Refresh hint only; never broadcast a due date to another tab.
    dueDateMayHaveChanged: boolean;
    // Only row, status, assignment, or linked-file changes can affect the personal badge.
    // This is a refresh hint; the server still computes the count and access scope.
    unfinishedCountMayHaveChanged: boolean;
    // Null means affected item IDs are unknown, as with default regeneration.
    items: readonly EvidenceRequirementAffectedItem[] | null;
}

export interface EvidenceRequirementInvalidation extends EvidenceRequirementChange {
    accountId: number;
}

interface EvidenceChangePacket {
    version: 3;
    senderId: string;
    eventId: string;
    accountId: number;
    change: EvidenceRequirementChange;
}

const CHANNEL_NAME = "cb4.evidence-requirement-changes.v3";
const MAX_ITEMS = 100;
const MAX_REQUIREMENT_KEYS = 100;
const MAX_ASSIGNEES = 1000;
const MAX_MESSAGE_CHARACTERS = 16000;
const MAX_SEEN_EVENTS = 128;

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
    const keys = Object.keys(value);
    return keys.length === expected.length && keys.every((key) => expected.includes(key));
}

function positiveId(value: unknown): value is number {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function validRequirementKey(value: unknown): value is string {
    if (typeof value !== "string" || !value || value.length > 510 ||
        value.trim() !== value || /[\x00-\x1f\x7f]/.test(value)) return false;
    let characters = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0)!;
        if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || ++characters > 255) return false;
    }
    return true;
}

function requirementKeyList(value: unknown): string[] | null | undefined {
    if (value === null) return null;
    if (!Array.isArray(value) || value.length > MAX_REQUIREMENT_KEYS ||
        value.some((entry) => !validRequirementKey(entry)) ||
        new Set(value).size !== value.length) return undefined;
    return [...value];
}

function userIdList(value: unknown): number[] | null | undefined {
    if (value === null) return null;
    if (!Array.isArray(value) || value.length > MAX_ASSIGNEES ||
        value.some((id) => !positiveId(id)) || new Set(value).size !== value.length) return undefined;
    return [...value];
}

function parseScope(value: unknown): EvidenceRequirementChangeScope | null | undefined {
    if (value === null) return null;
    if (!isRecord(value) || !hasExactKeys(value, ["requirementKeys", "assignedUserIds", "status"])) {
        return undefined;
    }
    const requirementKeys = requirementKeyList(value["requirementKeys"]);
    const assignedUserIds = userIdList(value["assignedUserIds"]);
    const status = value["status"];
    if (requirementKeys === undefined || assignedUserIds === undefined ||
        (status !== null && !isEvidenceWorkItemStatus(status))) return undefined;
    return { requirementKeys, assignedUserIds, status: isEvidenceWorkItemStatus(status) ? status : null };
}

function parseChange(value: unknown, allowConservativeFallback: boolean): EvidenceRequirementChange | null {
    if (!isRecord(value) || !hasExactKeys(value, [
        "kind", "productLicenseId", "dueDateMayHaveChanged", "unfinishedCountMayHaveChanged", "items"
    ])) {
        return null;
    }
    const kind = value["kind"];
    const productLicenseId = value["productLicenseId"];
    const dueDateMayHaveChanged = value["dueDateMayHaveChanged"];
    const unfinishedCountMayHaveChanged = value["unfinishedCountMayHaveChanged"];
    if (!positiveId(productLicenseId) ||
        typeof dueDateMayHaveChanged !== "boolean" ||
        typeof unfinishedCountMayHaveChanged !== "boolean" ||
        (kind !== "create" && kind !== "update" && kind !== "delete" &&
            kind !== "bulk" && kind !== "regenerate" && kind !== "upload" && kind !== "comment")) return null;
    const base: Omit<EvidenceRequirementChange, "items"> = {
        kind, productLicenseId, dueDateMayHaveChanged, unfinishedCountMayHaveChanged
    };
    if (value["items"] === null) return { ...base, items: null };
    if (!Array.isArray(value["items"]) || value["items"].length > MAX_ITEMS) {
        return allowConservativeFallback ? { ...base, items: null } : null;
    }
    const items: EvidenceRequirementAffectedItem[] = [];
    const seen = new Set<number>();
    for (const entry of value["items"]) {
        if (!isRecord(entry) || !hasExactKeys(entry, ["id", "before", "after"])) {
            return allowConservativeFallback ? { ...base, items: null } : null;
        }
        const id = entry["id"];
        if (!positiveId(id) || seen.has(id)) {
            return allowConservativeFallback ? { ...base, items: null } : null;
        }
        const before = parseScope(entry["before"]);
        const after = parseScope(entry["after"]);
        if (before === undefined || after === undefined) {
            return allowConservativeFallback ? { ...base, items: null } : null;
        }
        seen.add(id);
        items.push({ id, before, after });
    }
    return { ...base, items };
}

@Injectable({ providedIn: "root" })
export class EvidenceRequirementInvalidationService implements OnDestroy {
    private readonly changes = new Subject<EvidenceRequirementInvalidation>();
    private readonly senderId = typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    private readonly seenEvents = new Set<string>();
    private channel: BroadcastChannel | null = null;
    private sequence = 0;

    constructor(private readonly login: LoginService, private readonly zone: NgZone) {
        if (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined") {
            try {
                this.channel = new BroadcastChannel(CHANNEL_NAME);
                this.channel.onmessage = (message: MessageEvent<unknown>) => this.receive(message.data);
            } catch {
                // Same-tab invalidation remains available when the channel is blocked.
                this.channel = null;
            }
        }
    }

    watchChanges(): Observable<EvidenceRequirementInvalidation> {
        return this.changes.asObservable();
    }

    // Capture at subscription time for a write; a later login must not publish its result.
    capturePublisher(): (change: EvidenceRequirementChange) => void {
        const accountId = this.login.getCurrentAccountId();
        const userId = this.login.getCurrentUserId();
        const token = this.login.getToken();
        return (change) => {
            if (!positiveId(accountId) || !positiveId(userId) || !token ||
                this.login.getCurrentAccountId() !== accountId ||
                this.login.getCurrentUserId() !== userId || this.login.getToken() !== token) return;
            this.publish(accountId, change);
        };
    }

    ngOnDestroy(): void {
        this.channel?.close();
        this.channel = null;
        this.changes.complete();
    }

    private publish(accountId: number, input: EvidenceRequirementChange): void {
        let change = parseChange(input, true);
        if (!change) return;
        if (JSON.stringify(change).length > MAX_MESSAGE_CHARACTERS) {
            change = { kind: change.kind, productLicenseId: change.productLicenseId,
                dueDateMayHaveChanged: change.dueDateMayHaveChanged,
                unfinishedCountMayHaveChanged: change.unfinishedCountMayHaveChanged, items: null };
        }
        const packet: EvidenceChangePacket = {
            version: 3,
            senderId: this.senderId,
            eventId: `${this.senderId}:${++this.sequence}`,
            accountId,
            change
        };
        this.remember(packet.eventId);
        this.changes.next({ accountId, ...change });
        try {
            this.channel?.postMessage(packet);
        } catch {
            // A cross-tab delivery failure does not undo a successful write.
        }
    }

    private receive(value: unknown): void {
        if (!isRecord(value) || !hasExactKeys(value, ["version", "senderId", "eventId", "accountId", "change"])) {
            return;
        }
        const accountId = value["accountId"];
        const senderId = value["senderId"];
        const eventId = value["eventId"];
        if (value["version"] !== 3 || !positiveId(accountId) ||
            typeof senderId !== "string" || !senderId || senderId.length > 80 ||
            typeof eventId !== "string" || eventId.length > 120 ||
            !eventId.startsWith(`${senderId}:`) ||
            senderId === this.senderId || this.seenEvents.has(eventId) ||
            accountId !== this.login.getCurrentAccountId() ||
            !positiveId(this.login.getCurrentUserId()) || !this.login.getToken()) return;
        const change = parseChange(value["change"], false);
        if (!change || JSON.stringify(change).length > MAX_MESSAGE_CHARACTERS) return;
        this.remember(eventId);
        this.zone.run(() => this.changes.next({ accountId, ...change }));
    }

    private remember(eventId: string): void {
        this.seenEvents.add(eventId);
        if (this.seenEvents.size > MAX_SEEN_EVENTS) {
            const oldest = this.seenEvents.values().next().value;
            if (oldest !== undefined) this.seenEvents.delete(oldest);
        }
    }
}
